import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { showToast } from '../components/atoms/Toast/Toast';
import cidLogo from '../assets/images/cid.png';

const triggerDownload = (href, filename) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/* ============================================================
   CHARTE GRAPHIQUE DES EXPORTS (PDF + PNG)
   Une seule couleur de marque, celle du logo : bordeaux #551530.
   Le reste n'utilise que du blanc et des gris neutres.
   ============================================================ */

const BRAND_HEX = '#551530';
const BRAND_RGB = [85, 21, 48];
// Teinte très claire de la couleur de marque (fonds de cartes / filtres)
const BRAND_TINT_HEX = '#f8f2f5';
const BRAND_TINT_RGB = [248, 242, 245];
const BRAND_LINE = 'SecuFire Action · EDHEC Climate Innov';

/* ============================================================
   CHARGEMENT DU LOGO (cid.png)
   Chargé une seule fois, puis réutilisé par le PDF et les PNG.
   Si le logo est introuvable, les exports fonctionnent sans lui.
   ============================================================ */

const loadImageEl = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

let logoPromise = null;
const loadLogo = () => {
  if (!logoPromise) {
    logoPromise = loadImageEl(cidLogo)
      .then((img) => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);

        // En-têtes BLANCS : un logo clair sur fond transparent serait
        // invisible. Si les pixels opaques du logo sont majoritairement
        // clairs, on le recolore en bordeaux (la transparence est conservée).
        // Un logo qui a son propre fond foncé (ex. rectangle bordeaux) est
        // laissé tel quel.
        try {
          const imgData = ctx.getImageData(0, 0, c.width, c.height);
          const { data } = imgData;
          let sum = 0;
          let n = 0;
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] > 128) {
              sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
              n += 1;
            }
          }
          if (n && sum / n > 200) {
            for (let i = 0; i < data.length; i += 4) {
              data[i] = BRAND_RGB[0];
              data[i + 1] = BRAND_RGB[1];
              data[i + 2] = BRAND_RGB[2];
            }
            ctx.putImageData(imgData, 0, 0);
          }
        } catch (e) {
          console.warn('[export] analyse du logo impossible', e);
        }

        return {
          source: c,
          dataURL: c.toDataURL('image/png'),
          w: img.naturalWidth,
          h: img.naturalHeight,
        };
      })
      .catch((e) => {
        console.warn('[export] logo introuvable', e);
        logoPromise = null;
        return null;
      });
  }
  return logoPromise;
};

/* ============================================================
   NOMMAGE DES EXPORTS — standard unique pour toutes les pages
   (Logs d'authentification, SecuFire Action, etc.)
   Toujours : sfa-<date d'export>-<section>[-<sujet>][-<filtres actifs>]
   où <filtres actifs> peut contenir la période filtrée (si elle diffère
   de la simple date d'export), la hiérarchie admin sélectionnée, le
   filtre comptes internes/externes, etc.
   ============================================================ */

const removeAccents = (str) => String(str).normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const slugify = (str) => {
  if (!str) return '';
  return removeAccents(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const formatDateISO = (date) => {
  if (!date) return '';
  // Chaîne "AAAA-MM-JJ" : lue comme date locale (new Date("AAAA-MM-JJ")
  // est interprétée en UTC et peut décaler d'un jour selon le fuseau).
  const m = typeof date === 'string' && /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim());
  const d = date instanceof Date
    ? date
    : m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  // Format français JJ-MM-AAAA (ex: 25-09-2026), utilisé partout dans le
  // nommage des exports — jamais AAAA-MM-JJ.
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

/* Date lisible JJ/MM/AAAA (pour les textes affichés : en-tête PDF, etc.) */
const formatDateSlash = (date) => formatDateISO(date).replace(/-/g, '/');

/**
 * Construit la ligne "Période" affichée dans l'en-tête du PDF.
 * 3 cas : début + fin, début seul, fin seul. Retourne '' si aucune date.
 */
export const formatPeriodFR = (startDate, endDate) => {
  const s = formatDateSlash(startDate);
  const e = formatDateSlash(endDate);
  if (s && e) return `Période : ${s} au ${e}`;
  if (s) return `Depuis le ${s}`;
  if (e) return `Jusqu'au ${e}`;
  return '';
};

/**
 * Liste des filtres actifs sous forme de libellés lisibles, utilisée à la
 * fois pour l'en-tête du PDF (filtersLine) et pour le bandeau des PNG.
 *
 *   buildFilterLabels({ adminName, isInternal, startDate, endDate, extra: ['Services : Météo'] })
 *   → ['Vue globale', 'Tous comptes', 'Période : 01/01/2026 au 31/01/2026', 'Services : Météo']
 */
export const buildFilterLabels = ({
  adminName = null,
  isInternal = null,
  startDate = null,
  endDate = null,
  extra = [],
} = {}) => [
  adminName ? `Hiérarchie : ${adminName}` : 'Vue globale',
  isInternal === null || isInternal === undefined
    ? 'Tous comptes'
    : isInternal ? 'Comptes internes' : 'Comptes externes',
  formatPeriodFR(startDate, endDate) || 'Toutes périodes',
  ...extra,
].filter(Boolean);

/**
 * Construit un nom de fichier explicite et traçable pour TOUS les exports
 * (PNG / CSV / PDF) de toutes les pages de l'application.
 *
 * Format : sfa-<date-export>-<section>[-<sujet>][-<filtres>]
 *
 * - section : nom de la partie exportée (ex: "Evolution temporelle",
 *   "Repartition des endpoints", "rapport logs authentification"…)
 * - subject : sujet précis concerné, le cas échéant (ex: un utilisateur,
 *   un endpoint) — ajouté juste après la section
 * - startDate/endDate : période filtrée sur les données, si active.
 *   3 cas gérés, ajoutés dans les filtres :
 *     · début + fin    → periode-JJ-MM-AAAA-au-JJ-MM-AAAA
 *     · début seulement → depuis-JJ-MM-AAAA
 *     · fin seulement   → jusqu-au-JJ-MM-AAAA
 * - filters : autres filtres actifs à faire apparaître dans le nom, tels
 *   quels (ex: "admin-a-benali", "comptes-internes"…)
 *
 * Exemple : sfa-25-09-2026-rapport-logs-authentification-admin-a-benali-comptes-internes
 * Exemple : sfa-25-09-2026-profil-dactivite-j-dupont-periode-01-01-2026-au-31-01-2026
 * Exemple : sfa-25-09-2026-rapport-logs-depuis-01-09-2026
 * Exemple : sfa-25-09-2026-rapport-logs-jusqu-au-15-09-2026
 */
export const buildExportName = ({ section, subject, startDate, endDate, filters = [] } = {}) => {
  const parts = ['sfa', formatDateISO(new Date())];

  const sectionPart = section
    ? (subject ? `${slugify(section)}-${slugify(subject)}` : slugify(section))
    : subject ? slugify(subject) : null;
  if (sectionPart) parts.push(sectionPart);

  // Période : 3 cas (début + fin, début seul, fin seul)
  let periodPart = null;
  if (startDate && endDate) {
    periodPart = `periode-${formatDateISO(startDate)}-au-${formatDateISO(endDate)}`;
  } else if (startDate) {
    periodPart = `depuis-${formatDateISO(startDate)}`;
  } else if (endDate) {
    periodPart = `jusqu-au-${formatDateISO(endDate)}`;
  }

  [...filters, periodPart].filter(Boolean).forEach((f) => parts.push(slugify(f)));

  return parts.join('-');
};

/* ============================================================
   LIBELLÉS FRANÇAIS DES COLONNES
   Utilisés pour les en-têtes des CSV ET des tableaux PDF.
   Clés techniques de l'API → français. Les valeurs des données ne
   sont jamais modifiées, seuls les en-têtes sont traduits.
   ============================================================ */

const COLUMN_LABELS_FR = {
  user_id: 'Identifiant utilisateur',
  id: 'Identifiant',
  username: "Nom d'utilisateur",
  user_name: "Nom d'utilisateur",
  user: 'Utilisateur',
  full_name: 'Nom complet',
  first_name: 'Prénom',
  last_name: 'Nom de famille',
  name: 'Nom',
  email: 'Email',
  mail: 'Email',
  organisation: 'Organisation',
  organization: 'Organisation',
  active: 'Statut',
  is_active: 'Statut',
  status: 'Statut',
  role: 'Rôle',
  rank: 'Rang',
  phone: 'Téléphone',
  country: 'Pays',
  department: 'Département',
  account_type: 'Type de compte',
  total_usage: "Nombre d'appels",
  total_activities: "Total d'activités",
  activity_count: "Nombre d'activités",
  usage_count: "Nombre d'appels",
  endpoints_count: "Nombre d'endpoints",
  top_endpoint: 'Endpoint principal',
  top_functionality: 'Fonctionnalité principale',
  endpoint: 'Endpoint',
  date: 'Date',
  timestamp: 'Date et heure',
  ip: 'Adresse IP',
  ip_address: 'Adresse IP',
  method: 'Méthode',
  action: 'Action',
  type: 'Type',
  result: 'Résultat',
  description: 'Description',
  last_activity: 'Dernière activité',
  last_login: 'Dernière connexion',
  created_at: 'Créé le',
  updated_at: 'Modifié le',
};

const labelForKey = (key) => {
  const k = String(key);
  if (COLUMN_LABELS_FR[k]) return COLUMN_LABELS_FR[k];
  const s = k.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/* ============================================================
   NOMBRES DANS LE PDF
   toLocaleString('fr-FR') insère une espace fine insécable (U+202F)
   comme séparateur de milliers. La police Helvetica de jsPDF ne sait
   pas l'afficher et la remplace par un "/" (ex : 12523 → "12/523").
   On la remplace donc par une espace normale (ex : "12 523").
   ============================================================ */

const FIX_SPACES_RE = /[\u202F\u00A0]/g;

export const formatNumberFR = (n) =>
  Number(n ?? 0).toLocaleString('fr-FR').replace(FIX_SPACES_RE, ' ');

/* ============================================================
   DATES DANS LES TABLEAUX (CSV + PDF)
   Toute valeur qui ressemble à une date ISO (AAAA-MM-JJ, avec ou sans
   heure) ou tout objet Date est affiché au format français :
     · date seule     → JJ/MM/AAAA
     · date + heure   → JJ/MM/AAAA HH:MM:SS
   Passer DATE_YEAR_2_DIGITS à true pour obtenir JJ/MM/AA.
   ============================================================ */

const DATE_YEAR_2_DIGITS = false;

const pad2 = (n) => String(n).padStart(2, '0');
const fmtYear = (y) => (DATE_YEAR_2_DIGITS ? String(y).slice(-2) : String(y));

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const formatDateTimeFR = (dt) =>
  `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${fmtYear(dt.getFullYear())} `
  + `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}:${pad2(dt.getSeconds())}`;

const formatValueFR = (val) => {
  if (val instanceof Date) {
    return Number.isNaN(val.getTime()) ? '' : formatDateTimeFR(val);
  }
  if (typeof val !== 'string') return val;
  const s = val.trim();
  if (!ISO_DATE_RE.test(s)) return val;
  if (s.length === 10) {
    // Date seule : conversion directe du texte (aucun décalage de fuseau)
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${fmtYear(y)}`;
  }
  const dt = new Date(s.replace(' ', 'T'));
  return Number.isNaN(dt.getTime()) ? val : formatDateTimeFR(dt);
};

/* ============================================================
   PNG "DE MARQUE"
   Chaque capture PNG peut recevoir un habillage :
     ┌──────────────────────────────────────────────┐
     │ [logo]  TITRE DE LA SECTION                  │  bandeau bordeaux
     │         Sous-titre (page / microservice)     │
     ├──────────────────────────────────────────────┤
     │ (Vue globale) (Tous comptes) (Période : …)   │  puces des filtres actifs
     ├──────────────────────────────────────────────┤
     │              … capture du graphique …        │
     ├──────────────────────────────────────────────┤
     │ Exporté le JJ/MM/AAAA HH:MM · SecuFire …     │  pied de page
     └──────────────────────────────────────────────┘
   `meta` = { title, subtitle, filters: [string] }. Sans `meta`, la capture
   est exportée telle quelle (comportement historique).
   ============================================================ */

const PNG_SCALE = 2.5;
const CANVAS_FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';

const roundRectPath = (ctx, x, y, w, h, r) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

/* Réduit la taille d'un texte canvas jusqu'à ce qu'il tienne dans maxW. */
const fitCanvasText = (ctx, text, weight, startPx, minPx, maxW) => {
  let size = startPx;
  ctx.font = `${weight} ${size}px ${CANVAS_FONT}`;
  while (size > minPx && ctx.measureText(text).width > maxW) {
    size -= 1;
    ctx.font = `${weight} ${size}px ${CANVAS_FONT}`;
  }
  return size;
};

/* `source` : canvas ou <img> déjà chargé. Retourne un canvas complet
   (bandeau + filtres + capture + pied de page). */
const composeBrandedCanvas = async (source, meta = {}, scale = PNG_SCALE) => {
  const s = scale;
  const srcW = source.naturalWidth || source.width;
  const srcH = source.naturalHeight || source.height;
  const logo = await loadLogo();

  const W = Math.max(srcW, Math.round(560 * s));
  const pad = 18 * s;
  const headerH = 48 * s;
  const footerH = 20 * s;

  // ── Mise en page des puces de filtres (retour à la ligne automatique) ──
  const measure = document.createElement('canvas').getContext('2d');
  measure.font = `600 ${9 * s}px ${CANVAS_FONT}`;
  const chipH = 17 * s;
  const chipGap = 6 * s;
  const chipPadX = 8 * s;
  const rowGap = 5 * s;
  const maxRowW = W - pad * 2;

  const rows = [];
  let current = [];
  let currentW = 0;
  (meta.filters || []).filter(Boolean).forEach((label) => {
    const w = measure.measureText(label).width + chipPadX * 2;
    if (current.length && currentW + chipGap + w > maxRowW) {
      rows.push(current);
      current = [];
      currentW = 0;
    }
    currentW += (current.length ? chipGap : 0) + w;
    current.push({ label, w });
  });
  if (current.length) rows.push(current);

  const filtersH = rows.length
    ? rows.length * chipH + (rows.length - 1) * rowGap + 14 * s
    : 0;

  const H = headerH + filtersH + srcH + footerH;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'alphabetic';

  // Fond blanc
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  // ── En-tête BLANC : logo + titre en bordeaux, filet bordeaux dessous ──
  let textX = pad;
  if (logo) {
    let lh = headerH - 16 * s;
    let lw = (lh * logo.w) / logo.h;
    const maxLw = 100 * s;
    if (lw > maxLw) {
      lw = maxLw;
      lh = (lw * logo.h) / logo.w;
    }
    ctx.drawImage(logo.source, pad, (headerH - lh) / 2, lw, lh);
    textX = pad + lw + 12 * s;
  }

  const textMaxW = W - textX - pad;
  const title = meta.title || '';
  const subtitle = meta.subtitle || '';
  if (title) {
    ctx.fillStyle = BRAND_HEX;
    fitCanvasText(ctx, title, '700', 13.5 * s, 10 * s, textMaxW);
    ctx.fillText(title, textX, subtitle ? headerH / 2 - 2 * s : headerH / 2 + 5 * s);
  }
  if (subtitle) {
    ctx.fillStyle = 'rgba(85, 21, 48, 0.75)';
    fitCanvasText(ctx, subtitle, '400', 9 * s, 7 * s, textMaxW);
    ctx.fillText(subtitle, textX, headerH / 2 + 11 * s);
  }
  ctx.fillStyle = BRAND_HEX;
  ctx.fillRect(0, headerH - 2 * s, W, 2 * s);

  // ── Bandeau des filtres actifs ──
  if (rows.length) {
    ctx.fillStyle = BRAND_TINT_HEX;
    ctx.fillRect(0, headerH, W, filtersH);
    ctx.fillStyle = '#e6d6dd';
    ctx.fillRect(0, headerH + filtersH - 1 * s, W, 1 * s);

    let y = headerH + 7 * s;
    rows.forEach((row) => {
      let x = pad;
      row.forEach(({ label, w }) => {
        ctx.fillStyle = '#ffffff';
        roundRectPath(ctx, x, y, w, chipH, chipH / 2);
        ctx.fill();
        ctx.strokeStyle = BRAND_HEX;
        ctx.lineWidth = 1 * s;
        ctx.stroke();
        ctx.fillStyle = BRAND_HEX;
        ctx.font = `600 ${9 * s}px ${CANVAS_FONT}`;
        ctx.fillText(label, x + chipPadX, y + chipH / 2 + 3.2 * s);
        x += w + chipGap;
      });
      y += chipH + rowGap;
    });
  }

  // ── Capture (centrée si plus étroite que le bandeau) ──
  ctx.drawImage(source, (W - srcW) / 2, headerH + filtersH, srcW, srcH);

  // ── Pied de page ──
  const footerY = headerH + filtersH + srcH;
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(pad, footerY, W - pad * 2, 1 * s);
  ctx.fillStyle = '#94a3b8';
  ctx.font = `400 ${8.5 * s}px ${CANVAS_FONT}`;
  ctx.fillText(
    `Exporté le ${formatDateTimeFR(new Date())}  ·  ${BRAND_LINE}`,
    pad,
    footerY + footerH / 2 + 4 * s,
  );

  // Liseré de marque en bas
  ctx.fillStyle = BRAND_HEX;
  ctx.fillRect(0, H - 2 * s, W, 2 * s);

  return canvas;
};

/* ============================================================
   EXPORTS SIMPLES (PNG / CSV)
   `name` doit déjà être un nom complet (sans extension), construit
   via buildExportName().
   Le fichier est toujours généré, même sans données.
   ============================================================ */

/* Export d'un graphique ECharts (ref exposant getDataURL) en PNG.
   `meta` (facultatif) : { title, subtitle, filters } → ajoute le bandeau
   de marque avec les filtres actifs.
   ⚠️ Ne capture QUE ce que dessine echarts : si un élément (ex. un
   libellé "total" superposé en HTML/CSS au centre d'un donut) n'est
   pas dans l'option echarts elle-même, il ne sera jamais dans ce PNG.
   Pour ces cas-là, utiliser exportNodeAsPNG sur le conteneur DOM complet. */
export const exportEchartsPNG = async (chartRef, name = 'graphique', meta = null) => {
  try {
    const instance = chartRef?.current;
    const dataURL = instance?.getDataURL?.({
      type: 'png',
      pixelRatio: PNG_SCALE,
      backgroundColor: '#ffffff',
    });
    if (!dataURL) {
      showToast("Impossible d'exporter ce graphique", 'error');
      return;
    }
    let finalURL = dataURL;
    if (meta) {
      const img = await loadImageEl(dataURL);
      const canvas = await composeBrandedCanvas(img, meta);
      finalURL = canvas.toDataURL('image/png');
    }
    triggerDownload(finalURL, `${name}.png`);
    showToast('Image exportée', 'success');
  } catch (e) {
    console.error('[export] PNG (echarts)', e);
    showToast("Erreur lors de l'export PNG", 'error');
  }
};

/* Export d'un nœud DOM entier en PNG (capture tout ce qui est affiché,
   y compris les libellés/overlays HTML posés par-dessus un graphique).
   `meta` (facultatif) : même habillage de marque que exportEchartsPNG. */
export const exportNodeAsPNG = async (node, name = 'capture', meta = null) => {
  if (!node) {
    showToast('Élément introuvable', 'error');
    return;
  }
  try {
    const captured = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: PNG_SCALE,
      useCORS: true,
      logging: false,
    });
    const canvas = meta ? await composeBrandedCanvas(captured, meta) : captured;
    triggerDownload(canvas.toDataURL('image/png'), `${name}.png`);
    showToast('Image exportée', 'success');
  } catch (e) {
    console.error('[export] PNG (node)', e);
    showToast("Erreur lors de l'export PNG", 'error');
  }
};

/* Export d'un tableau d'objets (colonnes choisies à l'avance) en CSV.
   Les en-têtes sont traduits en français ; les valeurs sont lues avec
   les clés techniques d'origine. */
export const exportRowsAsCSV = (rows, name = 'export') => {
  try {
    const safeRows = Array.isArray(rows) ? rows : [];
    const headers = safeRows.length ? Object.keys(safeRows[0]) : [];
    const escapeCell = (val) => {
      const str = String(val ?? '');
      return /[",;\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [
      headers.map((h) => escapeCell(labelForKey(h))).join(';'),
      ...safeRows.map((row) => headers.map((h) => escapeCell(formatValueFR(row[h]))).join(';')),
    ];
    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${name}.csv`);
    URL.revokeObjectURL(url);
    showToast('CSV exporté', 'success');
  } catch (e) {
    console.error('[export] CSV', e);
    showToast("Erreur lors de l'export CSV", 'error');
  }
};

/* Export CSV "complet" : reprend TOUTES les clés présentes sur les objets
   (union des clés, dans l'ordre d'apparition), sans présélection de
   colonnes. Utilisé pour le classement des utilisateurs afin de ne perdre
   aucune information disponible côté API. En-têtes en français. */
export const exportFullObjectsAsCSV = (rows, name = 'export-complet') => {
  try {
    const safeRows = Array.isArray(rows) ? rows : [];
    const headerSet = [];
    safeRows.forEach((row) => {
      Object.keys(row || {}).forEach((k) => {
        if (!headerSet.includes(k)) headerSet.push(k);
      });
    });
    const flatten = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'boolean') return val ? 'oui' : 'non';
      if (val instanceof Date) return val;
      if (typeof val === 'object') return JSON.stringify(val);
      return val;
    };
    const escapeCell = (val) => {
      const str = String(val ?? '');
      return /[",;\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [
      headerSet.map((h) => escapeCell(labelForKey(h))).join(';'),
      ...safeRows.map((row) => headerSet.map((h) => escapeCell(formatValueFR(flatten(row?.[h])))).join(';')),
    ];
    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${name}.csv`);
    URL.revokeObjectURL(url);
    showToast('CSV exporté', 'success');
  } catch (e) {
    console.error('[export] CSV complet', e);
    showToast("Erreur lors de l'export CSV", 'error');
  }
};

/* ============================================================
   RAPPORT PDF PROFESSIONNEL
   Contrairement à l'ancien exportPageAsPDF (capture d'écran de la
   page), ce générateur reconstruit un vrai rapport de traçabilité :
   bandeau + synthèse chiffrée + tableaux de données. Aucune image de
   graphique n'est capturée : les courbes/camemberts sont retranscrits
   sous forme de tableaux exploitables et imprimables.
   ============================================================ */

const R_MARGIN = 40;
const R_HEADER_H = 70;
const R_FOOTER_H = 24;

/* Réduit la taille de police (jusqu'à minSize) pour que le texte tienne
   dans maxWidth. La police courante (normal/bold) doit être déjà définie. */
const fitFontSize = (pdf, text, maxWidth, startSize, minSize) => {
  let size = startSize;
  pdf.setFontSize(size);
  while (size > minSize && pdf.getTextWidth(String(text)) > maxWidth) {
    size -= 0.5;
    pdf.setFontSize(size);
  }
  return size;
};

/* Helvetica (jsPDF) ne gère que le jeu WinAnsi. Un seul caractère hors de ce
   jeu (flèche →, ≈, etc.) fait basculer TOUTE la chaîne en encodage UTF-16 :
   le texte s'affiche alors avec des espaces entre chaque lettre
   ("r a s t e r _ s e s s i o n") et déborde de sa colonne, tandis que la
   flèche devient "!'". On convertit donc ces caractères avant tout dessin.
   Les données ne sont modifiées que dans le PDF, jamais dans les CSV. */
const pdfSafe = (text) => String(text ?? '')
  .replace(/\s*[→➜➔⇒➝]\s*/g, ' -> ')
  .replace(/[≈≃]/g, '~')
  .replace(/[≤]/g, '<=')
  .replace(/[≥]/g, '>=')
  .replace(/[^\u0020-\u007E\u00A0-\u00FF\u2013\u2014\u2018\u2019\u201C\u201D\u2022\u2026\u20AC\n]/g, '?');

/* Date JavaScript "brute" (résultat de String(date) / date.toString()),
   ex : "Wed Oct 14 2026 00:00:00 GMT+0100 (heure normale d'Europe centrale)".
   Si une telle date se retrouve concaténée dans un texte du rapport, on la
   convertit en JJ/MM/AAAA pour éviter une ligne interminable. */
const RAW_DATE_RE = /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun) [A-Z][a-z]{2} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{4}(?: \([^)]*\))?/g;

/* Normalisation des textes affichés dans le PDF (titres, libellés, pied
   de page) :
   - dates JS brutes → JJ/MM/AAAA
   - flèches (→ ➜ ➔), absentes de Helvetica (affichées "!'") → " au "
   - "SecuFire" seul → "SecuFire Action"
   - "auth" abrégé → "authentification"
   Les données des tableaux ne sont jamais modifiées. */
const fr = (text) => String(text ?? '')
  .replace(RAW_DATE_RE, (m) => {
    const d = new Date(m.replace(/\s*\([^)]*\)\s*$/, ''));
    return Number.isNaN(d.getTime()) ? m : formatDateSlash(d);
  })
  .replace(/\s*[→➜➔]\s*/g, ' au ')
  .replace(/\bSecuFire\b(?!\s+Action)/gi, 'SecuFire Action')
  .replace(/\bauth\b/gi, 'authentification');

const frSafe = (text) => pdfSafe(fr(text));

const drawReportHeader = (pdf, { title: rawTitle, subtitle: rawSubtitle, filtersLine: rawFilters, accentColor, pageWidth, logo }) => {
  const title = frSafe(rawTitle);
  const subtitle = rawSubtitle ? frSafe(rawSubtitle) : '';
  const filtersLine = rawFilters ? frSafe(rawFilters) : '';

  // ── En-tête BLANC : logo + textes en bordeaux, filet bordeaux dessous ──
  let textX = R_MARGIN;
  if (logo) {
    const boxH = 46;
    let lh = boxH;
    let lw = (lh * logo.w) / logo.h;
    const maxLw = 120;
    if (lw > maxLw) {
      lw = maxLw;
      lh = (lw * logo.h) / logo.w;
    }
    try {
      pdf.addImage(logo.dataURL, 'PNG', R_MARGIN, (R_HEADER_H - lh) / 2, lw, lh);
    } catch (e) {
      console.warn('[export] logo non ajouté au PDF', e);
    }
    textX = R_MARGIN + lw + 16;
  }

  pdf.setTextColor(...accentColor);
  pdf.setFont('helvetica', 'bold');
  // Le titre ne doit jamais chevaucher la date de génération (à droite)
  const titleMaxW = pageWidth - textX - R_MARGIN - 130;
  pdf.setFontSize(fitFontSize(pdf, title, titleMaxW, 15, 10));
  pdf.text(title, textX, 28);

  // Date de génération : alignée à droite sur la ligne du titre, donc
  // jamais en conflit avec la ligne des filtres (en bas du bandeau).
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, pageWidth - R_MARGIN, 28, { align: 'right' });

  pdf.setFontSize(9);
  if (subtitle) pdf.text(subtitle, textX, 42);
  if (filtersLine) {
    const maxW = pageWidth - textX - R_MARGIN;
    const size = fitFontSize(pdf, filtersLine, maxW, 9, 6.5);
    pdf.setFontSize(size);
    if (pdf.getTextWidth(filtersLine) <= maxW) {
      pdf.text(filtersLine, textX, 56);
    } else {
      // Trop long même en petit : retour à la ligne (3 lignes max)
      pdf.text(pdf.splitTextToSize(filtersLine, maxW).slice(0, 3), textX, 52);
    }
  }

  // Filet bordeaux sous l'en-tête
  pdf.setDrawColor(...accentColor);
  pdf.setLineWidth(1.5);
  pdf.line(R_MARGIN, R_HEADER_H, pageWidth - R_MARGIN, R_HEADER_H);
};

const drawReportFooter = (pdf, { pageWidth, pageHeight, pageNum, totalPages, brandLine }) => {
  pdf.setDrawColor(225, 225, 225);
  pdf.line(R_MARGIN, pageHeight - R_FOOTER_H, pageWidth - R_MARGIN, pageHeight - R_FOOTER_H);
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(brandLine, R_MARGIN, pageHeight - 10);
  pdf.text(`Page ${pageNum} / ${totalPages}`, pageWidth - R_MARGIN, pageHeight - 10, { align: 'right' });
};

class ReportCursor {
  constructor(pdf, opts) {
    this.pdf = pdf;
    this.opts = opts;
    this.pageWidth = pdf.internal.pageSize.getWidth();
    this.pageHeight = pdf.internal.pageSize.getHeight();
    this.y = R_HEADER_H + 24;
    drawReportHeader(pdf, { ...opts, pageWidth: this.pageWidth });
  }

  ensureSpace(h, onBreak) {
    if (this.y + h > this.pageHeight - R_FOOTER_H - 10) {
      // Le bandeau d'en-tête n'apparaît qu'en première page : les pages
      // suivantes repartent directement depuis la marge du haut.
      this.pdf.addPage();
      this.y = R_MARGIN;
      if (onBreak) onBreak();
    }
  }

  sectionTitle(text) {
    this.ensureSpace(30);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(...this.opts.accentColor);
    this.pdf.text(pdfSafe(text), R_MARGIN, this.y);
    this.y += 8;
    this.pdf.setDrawColor(...this.opts.accentColor);
    this.pdf.setLineWidth(1.2);
    this.pdf.line(R_MARGIN, this.y, this.pageWidth - R_MARGIN, this.y);
    this.y += 16;
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(60, 60, 60);
  }

  paragraph(text) {
    this.pdf.setFontSize(9.5);
    this.pdf.setTextColor(70, 70, 70);
    const lines = this.pdf.splitTextToSize(pdfSafe(text), this.pageWidth - R_MARGIN * 2);
    lines.forEach((line) => {
      this.ensureSpace(14);
      this.pdf.text(line, R_MARGIN, this.y);
      this.y += 14;
    });
    this.y += 6;
  }

  kpiRow(rawItems) {
    // Nettoyage des espaces insécables (affichées "/" par Helvetica)
    const items = rawItems.map((it) => ({
      ...it,
      value: pdfSafe(String(it.value).replace(FIX_SPACES_RE, ' ')),
      label: pdfSafe(it.label),
    }));
    const gap = 10;
    const boxW = (this.pageWidth - R_MARGIN * 2 - gap * (items.length - 1)) / items.length;
    const boxH = 44;
    this.ensureSpace(boxH + 12);
    items.forEach((it, i) => {
      const x = R_MARGIN + i * (boxW + gap);
      this.pdf.setFillColor(...BRAND_TINT_RGB);
      this.pdf.setDrawColor(230, 214, 221);
      this.pdf.roundedRect(x, this.y, boxW, boxH, 3, 3, 'FD');
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(fitFontSize(this.pdf, it.value, boxW - 20, 13, 7));
      this.pdf.setTextColor(...this.opts.accentColor);
      this.pdf.text(String(it.value), x + 10, this.y + 22);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(fitFontSize(this.pdf, it.label, boxW - 20, 8, 6));
      this.pdf.setTextColor(120, 120, 120);
      this.pdf.text(String(it.label), x + 10, this.y + 34);
    });
    this.y += boxH + 20;
  }

  /* Découpe le texte d'une cellule en lignes (retour à la ligne), sans
     jamais tronquer ni ajouter de "…". Un mot plus long que la colonne
     est coupé caractère par caractère. */
  wrapCell(text, maxWidth, fontSize) {
    const pdf = this.pdf;
    pdf.setFontSize(fontSize);
    const lines = [];
    pdf.splitTextToSize(String(text), maxWidth).forEach((line) => {
      if (pdf.getTextWidth(line) <= maxWidth) {
        lines.push(line);
        return;
      }
      let current = '';
      Array.from(line).forEach((ch) => {
        if (current && pdf.getTextWidth(current + ch) > maxWidth) {
          lines.push(current);
          current = ch;
        } else {
          current += ch;
        }
      });
      if (current) lines.push(current);
    });
    return lines.length ? lines : [''];
  }

  /* Prépare une cellule : retour à la ligne, et réduction de la police
     (8.5 → 6.5) tant que le texte dépasse `maxLines` lignes. */
  layoutCell(text, maxWidth, startSize = 8.5, minSize = 6.5, maxLines = 2) {
    let size = startSize;
    let lines = this.wrapCell(text, maxWidth, size);
    while (lines.length > maxLines && size > minSize) {
      size -= 0.5;
      lines = this.wrapCell(text, maxWidth, size);
    }
    return { lines, size };
  }

  /* Cellule "fit" : le texte complet reste TOUJOURS sur une seule ligne,
     jamais coupé ni tronqué. La police ne se réduit (jusqu'à minSize)
     que si le texte est vraiment trop long pour la colonne. */
  layoutFit(text, maxWidth, startSize = 8.5, minSize = 5) {
    const size = fitFontSize(this.pdf, text, maxWidth, startSize, minSize);
    return { lines: [String(text)], size };
  }

  /* Colonnes : { key, label, width?, fit? }
     - width : poids relatif (colonnes flexibles)
     - fit   : true → la colonne est élargie pour afficher son contenu
               complet sur une seule ligne (ex: email, nom d'utilisateur)
     - maxLines : lignes autorisées avant réduction de la police (défaut 2) ;
               à augmenter pour une colonne à contenu long (ex: Détails) */
  table({ columns, rows, zebra = true }) {
    if (!rows.length) {
      this.paragraph('Aucune donnée disponible.');
      return;
    }

    const totalW = this.pageWidth - R_MARGIN * 2;
    const cellText = (raw) => {
      if (raw === null || raw === undefined || raw === '') return '—';
      // Les espaces insécables s'afficheraient "/" dans le PDF : on les normalise.
      return pdfSafe(String(formatValueFR(raw)).replace(FIX_SPACES_RE, ' '));
    };

    // Largeur nécessaire pour les colonnes "fit" (texte complet sur 1 ligne)
    this.pdf.setFont('helvetica', 'bold');
    const labelWidths = columns.map((c) => (c.fit ? this.pdf.getTextWidth(pdfSafe(c.label)) : 0));
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(8.5);
    let need = columns.map((c, i) => (c.fit
      ? Math.max(
          labelWidths[i],
          ...rows.map((r) => this.pdf.getTextWidth(cellText(r[c.key]))),
        ) + 16
      : 0));
    const fixedTotal = need.reduce((a, b) => a + b, 0);
    const flexRatio = columns.reduce((a, c) => a + (c.fit ? 0 : c.width || 1), 0);

    let widths;
    if (flexRatio === 0) {
      // Toutes les colonnes sont en "fit" : on répartit toute la largeur
      // proportionnellement à leur besoin.
      widths = need.map((n) => (n / (fixedTotal || 1)) * totalW);
    } else {
      const maxFixed = totalW * 0.65; // garde de la place pour les autres colonnes
      if (fixedTotal > maxFixed) need = need.map((n) => (n * maxFixed) / fixedTotal);
      const fixedSum = need.reduce((a, b) => a + b, 0);
      widths = columns.map((c, i) => (c.fit
        ? need[i]
        : ((c.width || 1) / flexRatio) * (totalW - fixedSum)));
    }

    const innerW = (i) => widths[i] - 12;
    const minRowH = 18;
    const lineHeight = (size) => size * 1.2;
    const heightOf = (cells) => Math.max(
      minRowH,
      ...cells.map((c) => c.lines.length * lineHeight(c.size) + 8),
    );
    const drawCellLines = (cell, x) => {
      this.pdf.setFontSize(cell.size);
      cell.lines.forEach((line, l) => {
        this.pdf.text(line, x + 6, this.y + 5 + cell.size * 0.9 + l * lineHeight(cell.size));
      });
    };

    // En-tête : calculé une seule fois, redessiné à chaque saut de page
    this.pdf.setFont('helvetica', 'bold');
    const headerCells = columns.map((c, i) => (c.fit
      ? this.layoutFit(pdfSafe(c.label), innerW(i))
      : this.layoutCell(pdfSafe(c.label), innerW(i))));
    const headerH = heightOf(headerCells);

    const drawHeaderRow = () => {
      this.pdf.setFillColor(...this.opts.accentColor);
      this.pdf.rect(R_MARGIN, this.y, totalW, headerH, 'F');
      this.pdf.setTextColor(255, 255, 255);
      this.pdf.setFont('helvetica', 'bold');
      let x = R_MARGIN;
      headerCells.forEach((cell, i) => {
        drawCellLines(cell, x);
        x += widths[i];
      });
      this.y += headerH;
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(60, 60, 60);
    };

    this.ensureSpace(headerH + minRowH);
    drawHeaderRow();

    rows.forEach((row, rIdx) => {
      this.pdf.setFont('helvetica', 'normal');
      const cells = columns.map((c, i) => {
        const text = cellText(row[c.key]);
        return c.fit
          ? this.layoutFit(text, innerW(i))
          : this.layoutCell(text, innerW(i), 8.5, 6.5, c.maxLines || 2);
      });
      const rowH = heightOf(cells);
      this.ensureSpace(rowH, drawHeaderRow);
      if (zebra && rIdx % 2 === 1) {
        this.pdf.setFillColor(...BRAND_TINT_RGB);
        this.pdf.rect(R_MARGIN, this.y, totalW, rowH, 'F');
      }
      this.pdf.setTextColor(60, 60, 60);
      let x = R_MARGIN;
      cells.forEach((cell, i) => {
        drawCellLines(cell, x);
        x += widths[i];
      });
      this.y += rowH;
    });
    this.y += 14;
  }

  finalize() {
    const total = this.pdf.internal.getNumberOfPages();
    for (let p = 1; p <= total; p += 1) {
      this.pdf.setPage(p);
      drawReportFooter(this.pdf, {
        pageWidth: this.pageWidth,
        pageHeight: this.pageHeight,
        pageNum: p,
        totalPages: total,
        brandLine: this.opts.brandLine,
      });
    }
  }
}

/**
 * Génère un vrai rapport PDF de traçabilité (pas une capture d'écran) :
 * - en-tête blanc avec le logo, le titre en bordeaux, le sous-titre, les filtres
 *   actifs et la date de génération
 * - synthèse chiffrée (KPI)
 * - tableau de l'évolution temporelle
 * - tableau de répartition des endpoints
 * - tableau complet du classement des utilisateurs (en-têtes en français,
 *   email et nom d'utilisateur affichés en entier sur une seule ligne)
 * - le cas échéant, profil d'activité détaillé d'un utilisateur (texte +
 *   tableau d'usage par endpoint — jamais la courbe ni le camembert)
 *
 * La charte graphique est unique (bordeaux du logo) : le paramètre
 * `accentColor` n'existe plus, la couleur est définie dans ce fichier.
 *
 * ASYNCHRONE (chargement du logo) : toujours l'appeler avec `await`.
 * Le PDF est toujours généré, même sans données (les tableaux vides
 * affichent « Aucune donnée disponible. »). Retourne true si le PDF a été
 * généré, false en cas d'erreur.
 */
export const generateSecuFireReport = async ({
  fileName,
  title = 'Rapport SecuFire Action',
  subtitle = '',
  filtersLine = '',
  // Texte affiché en pied de page de chaque page du rapport. Toujours
  // écrire "SecuFire Action" (jamais "SecuFire" seul) dans le contenu.
  brandLine = BRAND_LINE,
  // `kpis` (tableau [{label, value}]) prend le pas sur `summary` si fourni :
  // à utiliser pour toute page qui n'a pas le schéma "utilisateurs" ci-dessous.
  kpis = null,
  summary = {},
  dailyTrend = [],
  endpointDistribution = [],
  endpointDistributionTitle = 'Répartition des endpoints',
  endpointDistributionColumnLabel = 'Endpoint',
  users = [],
  selectedUser = null,
  selectedUserEndpoints = [],
  // Sections libres ajoutées après les sections standards ci-dessus :
  // [{ title, paragraph?, table?: { columns, rows } }]
  extraSections = [],
}) => {
  try {
    const accentColor = BRAND_RGB;
    const logo = await loadLogo();

    // Format paysage : indispensable pour restituer des tableaux larges
    // (utilisateurs, endpoints, historiques d'appels…) de façon lisible.
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const cursor = new ReportCursor(pdf, { title, subtitle, filtersLine, accentColor, brandLine, logo });

    const kpiItems = kpis || (summary && Object.keys(summary).length
      ? [
          { label: 'Total activités', value: formatNumberFR(summary.totalActivities) },
          { label: 'Utilisateurs actifs', value: formatNumberFR(summary.activeUsers) },
          { label: 'Total utilisateurs', value: formatNumberFR(summary.totalUsers ?? users.length) },
          { label: 'Top fonctionnalité', value: summary.topFunctionality || 'N/A' },
        ]
      : []);
    if (kpiItems.length) {
      cursor.sectionTitle('Synthèse');
      cursor.kpiRow(kpiItems);
    }

    if (dailyTrend.length) {
      cursor.sectionTitle('Évolution temporelle des activités');
      cursor.table({
        columns: [
          { key: 'date', label: 'Date', width: 1 },
          { key: 'total_activities', label: "Total d'activités", width: 1 },
        ],
        rows: dailyTrend.map((d) => ({
          date: d.date,
          total_activities: formatNumberFR(d.total_activities),
        })),
      });
    }

    if (endpointDistribution.length) {
      cursor.sectionTitle(endpointDistributionTitle);
      const total = endpointDistribution.reduce((s, e) => s + (e.total_usage || 0), 0) || 1;
      cursor.table({
        columns: [
          { key: 'endpoint', label: endpointDistributionColumnLabel, width: 2 },
          { key: 'total_usage', label: 'Appels', width: 1 },
          { key: 'part', label: 'Part', width: 1 },
        ],
        rows: endpointDistribution.map((e) => ({
          endpoint: e.endpoint,
          total_usage: formatNumberFR(e.total_usage),
          part: `${((100 * (e.total_usage || 0)) / total).toFixed(1)} %`,
        })),
      });
    }

    if (users.length) {
      cursor.sectionTitle(`Classement des utilisateurs — tableau complet (${users.length})`);
      // Toutes les clés disponibles sur les objets utilisateurs (limitées à 9
      // colonnes pour rester lisible même en paysage ; augmenter si besoin).
      const keys = Array.from(
        users.reduce((set, u) => {
          Object.keys(u || {}).forEach((k) => set.add(k));
          return set;
        }, new Set()),
      ).slice(0, 9);
      // Colonnes élargies : contenu complet sur une seule ligne.
      const FIT_KEYS = ['email', 'mail', 'username', 'user_name'];
      cursor.table({
        columns: keys.map((k) => ({
          key: k,
          label: labelForKey(k),
          width: 1,
          fit: FIT_KEYS.includes(k),
        })),
        rows: users.map((u) => {
          const row = {};
          keys.forEach((k) => {
            const v = u[k];
            row[k] = typeof v === 'boolean' ? (v ? 'Oui' : 'Non') : v;
          });
          return row;
        }),
      });
    }

    if (selectedUser) {
      cursor.sectionTitle(`Profil d'activité — ${selectedUser.username || selectedUser.user_id}`);
      cursor.paragraph(
        [
          `Identifiant : ${selectedUser.user_id ?? 'N/A'}`,
          `Statut : ${selectedUser.active ? 'Actif' : 'Inactif'}`,
          selectedUser.email ? `Email : ${selectedUser.email}` : null,
          selectedUser.organisation ? `Organisation : ${selectedUser.organisation}` : null,
        ]
          .filter(Boolean)
          .join('   ·   '),
      );
      if (selectedUserEndpoints.length) {
        cursor.table({
          columns: [
            { key: 'endpoint', label: 'Endpoint utilisé', width: 2 },
            { key: 'total_usage', label: 'Appels', width: 1 },
          ],
          rows: selectedUserEndpoints.map((e) => ({
            endpoint: e.endpoint,
            total_usage: formatNumberFR(e.total_usage),
          })),
        });
      } else {
        cursor.paragraph("Aucune donnée d'activité détaillée disponible pour la période sélectionnée.");
      }
    }

    extraSections.forEach((sec) => {
      if (!sec) return;
      cursor.sectionTitle(sec.title);
      if (sec.paragraph) cursor.paragraph(sec.paragraph);
      if (sec.table) cursor.table(sec.table);
    });

    cursor.finalize();
    pdf.save(`${fileName}.pdf`);
    showToast('Rapport PDF généré', 'success');
    return true;
  } catch (e) {
    console.error('[export] PDF rapport', e);
    showToast("Erreur lors de la génération du rapport PDF", 'error');
    return false;
  }
};